import { useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface PaymentTerm {
  id: string;
  code: string;
  name: string;
  netDays: number;
  discountPct: string | null;
  discountDays: number | null;
  isActive: boolean;
  isDefault: boolean;
}

export default function PaymentTermsSettingsPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    code: "", name: "", netDays: 30,
    discountPct: "0.00", discountDays: 0,
    isActive: true, isDefault: false,
  });

  const { data: terms = [] } = useQuery<PaymentTerm[]>({
    queryKey: ["/api/payment-terms", { includeInactive: "true" }],
    queryFn: () => api.get<PaymentTerm[]>(`/payment-terms?includeInactive=true`, sessionId),
  });

  const saveMutation = useMutation({
    mutationFn: () => editingId
      ? api.patch(`/payment-terms/${editingId}`, form, sessionId)
      : api.post(`/payment-terms`, form, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/payment-terms"] });
      setDialogOpen(false);
      setEditingId(null);
      setForm({ code: "", name: "", netDays: 30, discountPct: "0.00", discountDays: 0, isActive: true, isDefault: false });
      toast({ title: editingId ? "Payment term updated" : "Payment term created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/payment-terms/${id}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/payment-terms"] });
      toast({ title: "Payment term deleted" });
    },
  });

  const handleEdit = (t: PaymentTerm) => {
    setEditingId(t.id);
    setForm({
      code: t.code, name: t.name, netDays: t.netDays,
      discountPct: t.discountPct || "0.00", discountDays: t.discountDays || 0,
      isActive: t.isActive, isDefault: t.isDefault,
    });
    setDialogOpen(true);
  };

  const handleNew = () => {
    setEditingId(null);
    setForm({ code: "", name: "", netDays: 30, discountPct: "0.00", discountDays: 0, isActive: true, isDefault: false });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payment Terms"
        description="Configure payment terms used on quotes, orders, and invoices"
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={handleNew} data-testid="button-new-payment-term">
              <Plus className="h-4 w-4 mr-1" /> New Payment Term
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <CreditCard className="h-4 w-4" /> All Payment Terms ({terms.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {terms.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No payment terms configured.</p>
          ) : (
            <div className="space-y-2">
              {terms.map((t) => (
                <div
                  key={t.id}
                  className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/40"
                  data-testid={`payment-term-row-${t.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-medium text-muted-foreground">{t.code}</span>
                      <span className="font-medium text-sm">{t.name}</span>
                      {t.isDefault && <Badge className="text-[10px]">Default</Badge>}
                      {!t.isActive && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Net {t.netDays} days
                      {t.discountPct && Number(t.discountPct) > 0 &&
                        ` · ${t.discountPct}% if paid within ${t.discountDays} days`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(t)}>Edit</Button>
                    <Button
                      variant="ghost" size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => deleteMutation.mutate(t.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Payment Term" : "New Payment Term"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Code *</label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="NET30"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Net Days *</label>
                <Input
                  type="number" min={0}
                  value={form.netDays}
                  onChange={(e) => setForm({ ...form, netDays: Number(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Name *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Net 30"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Early-Pay Discount %</label>
                <Input
                  type="number" step="0.01" min={0}
                  value={form.discountPct}
                  onChange={(e) => setForm({ ...form, discountPct: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Discount Days</label>
                <Input
                  type="number" min={0}
                  value={form.discountDays}
                  onChange={(e) => setForm({ ...form, discountDays: Number(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
                Active
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isDefault}
                  onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
                />
                Default
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={!form.code || !form.name || saveMutation.isPending}
              >
                {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingId ? "Save Changes" : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
